import { useEffect, useRef, useState } from "react";
import { teacherApi } from "@/services/api/teacher";
import { teacherPriorityApi } from "@/services/api/teacherPriority";
import { getTakenOverLessonIds } from "@/utils/prepRequestLessons";
import type { AdminSubstitutionDetail } from "@/services/api/admin";
import { substitutionRoomLabel } from "@/utils/lessonRoom";
import { sortLessonsByDateTime } from "@/utils/lessonSort";
import { useAuthStore } from "@/store";
import { useDirectorEnabled } from "@/hooks/useDirectorEnabled";

import {
  TalkRadarDock,
  TalkRadarLessonStorno,
  TalkRadarStornoConfirm,
} from "./TalkRadarStorno";
import { useTalkT } from "./useTalkT";
import { useTalkRadarDetailLoad } from "./useTalkRadarDetailLoad";
import { useTalkRadarRespond } from "./useTalkRadarRespond";
import { useTalkRadarStorno } from "./useTalkRadarStorno";
import {
  confirmAsk,
  decidedLabel,
  formatDay,
  lessonStatusWord,
  teacherLessonRows,
  teacherNote,
  useRadarStatusLabel,
  answerLabel,
  type LessonChoice,
  type LessonRow,
} from "./talkRadarReadFormat";
import { TalkPrepBand } from "./talkPrepBand";
import { TalkIncomingFinance, TalkOwnDesktop } from "./TalkFinanceBand";
import { financeUiEnabled } from "@/utils/financeUi";

function LessonTable({
  assignLabel,
  changeLabel,
  lessons,
  owner = false,
  teacherActions,
  myTeacherId,
  pending,
  onChoose,
  onCancel,
  onConfirm,
  onAssignLesson,
  onCancelLesson,
  cancelFilled = false,
  onWithdraw,
  onTeacherStorno,
  onRemoveLesson,
  hrOn = false,
  adminRead = false,
  headers: _headers,
  packageHint,
}: {
  lessons: LessonRow[];
  owner?: boolean;
  teacherActions?: boolean;
  packageHint?: boolean;
  myTeacherId?: string | null;
  pending: { lessonId: string; choice: LessonChoice } | null;
  onChoose: (lessonId: string, choice: LessonChoice) => void;
  onCancel: () => void;
  onConfirm: () => void;
  onAssignLesson?: (
    lessonId: string,
    assignedTeacherName?: string | null
  ) => void;
  onCancelLesson?: (lessonId: string) => void;
  cancelFilled?: boolean;
  onWithdraw?: (lessonId: string) => void;
  onTeacherStorno?: (lessonId: string) => void;
  onRemoveLesson?: (lessonId: string) => void;
  hrOn?: boolean;
  adminRead?: boolean;
  assignLabel: string;
  changeLabel: string;
  headers: {
    date: string;
    time: string;
    subject: string;
    klass: string;
    action: string;
  };
}) {
  const t = useTalkT();
  const rows = sortLessonsByDateTime(
    teacherActions
      ? lessons.filter((lesson) => !isDirectorRemoved(lesson))
      : lessons
  );
  const roomPerspective = owner ? "owner" : teacherActions ? "viewer" : "admin";
  const wordMode = owner ? "owner" : teacherActions ? "viewer" : "admin";
  return (
    <div className="talk-lessons">
      {rows.map((lesson, index) => {
        const asking = pending?.lessonId === lesson.id;
        const decided = decidedLabel(lesson);
        const room = substitutionRoomLabel(
          lesson,
          roomPerspective,
          myTeacherId
        );
        const struck = isDeadAdminLesson(lesson);
        const substitute =
          lesson.assigned_teacher_name || lesson.assigned_teacher_short_code;
        const state = lesson.assignment_state || lesson.status;
        const dateLabel = formatDay(lesson.date);
        const showDate =
          index === 0 || formatDay(rows[index - 1]?.date) !== dateLabel;
        const declined =
          state === "declined" ||
          lesson.my_response === "declined" ||
          lesson.status === "declined";
        const canAnswer = !!teacherActions && !owner;
        const canTeacherStorno =
          canAnswer &&
          !struck &&
          (state === "waiting" ||
            state === "confirmed" ||
            state === "booked" ||
            lesson.status === "confirmed" ||
            lesson.status === "booked");
        const showHr =
          hrOn &&
          (lesson.confirmed_at ||
            lesson.status === "booked" ||
            lesson.assignment_state === "booked");
        const coveredCode = owner ? coveredShortCode(lesson) : null;
        const text = (value: string) =>
          struck ? <s>{value}</s> : <span>{value}</span>;
        const kind = struck
          ? "cancelled"
          : state === "confirmed" || state === "booked"
            ? "accepted"
            : state === "waiting" || state === "interested"
              ? "interested"
              : state === "declined"
                ? "declined"
                : "open";
        return (
          <article
            key={lesson.id}
            className={`talk-lesson is-${kind}`}
            aria-label={lesson.subject_name || "—"}
          >
            <time>
              {showDate ? text(dateLabel) : null}{" "}
              {text(lesson.time_slot_name || "—")}
            </time>
            <strong>{text(lesson.subject_name || "—")}</strong>
            <p>
              {text(lesson.klass_name || "—")}
              {room ? <> {room}</> : null}
            </p>
            <div className="talk-radar-act">
              {teacherActions ? (
                <>
                  {canAnswer &&
                    !decided &&
                    lesson.can_respond &&
                    asking &&
                    pending && (
                      <>
                        <span>{confirmAsk(pending.choice)}</span>{" "}
                        <button type="button" onClick={onConfirm}>
                          Bestätigen
                        </button>{" "}
                        <button type="button" onClick={onCancel}>
                          Abbrechen
                        </button>
                      </>
                    )}
                  {canAnswer && !decided && lesson.can_respond && !asking && (
                    <>
                      {packageHint && lesson.block_type === "partial" ? (
                        <span>{t("talk_radar_partial")}</span>
                      ) : packageHint && lesson.block_type === "full_day" ? (
                        <span>{t("talk_radar_full_day")}</span>
                      ) : null}{" "}
                      <button
                        type="button"
                        onClick={() => onChoose(lesson.id, "interested")}
                      >
                        Interessiert
                      </button>{" "}
                      <button
                        type="button"
                        onClick={() => onChoose(lesson.id, "decline")}
                      >
                        Ablehnen
                      </button>
                    </>
                  )}
                  {coveredCode && <span>{coveredCode}</span>}
                  {!coveredCode &&
                    (owner || !lesson.can_respond || decided) && (
                      <span>
                        {t(lessonStatusWord(lesson, myTeacherId, wordMode))}
                      </span>
                    )}
                  {canAnswer && declined && onWithdraw && (
                    <button type="button" onClick={() => onWithdraw(lesson.id)}>
                      {t("talk_radar_withdraw")}
                    </button>
                  )}
                  {canTeacherStorno && onTeacherStorno && (
                    <button
                      type="button"
                      onClick={() => onTeacherStorno(lesson.id)}
                    >
                      {t("talk_radar_storno")}
                    </button>
                  )}
                  {showHr && <p>{t("talk_radar_hr_lesson")}</p>}
                  {owner &&
                    !struck &&
                    onRemoveLesson &&
                    canOwnerRemove(lesson) && (
                      <button
                        type="button"
                        onClick={() => onRemoveLesson(lesson.id)}
                      >
                        {t("talk_radar_remove")}
                      </button>
                    )}
                </>
              ) : adminRead && struck ? (
                <span>{t("talk_radar_banner_cancelled")}</span>
              ) : (
                <>
                  {text(
                    lesson.assigned_teacher_name ||
                      lesson.assigned_teacher_short_code ||
                      "—"
                  )}{" "}
                  {text(t(lessonStatusWord(lesson, myTeacherId, wordMode)))}
                </>
              )}
              {!struck && onAssignLesson && (
                <button
                  type="button"
                  onClick={() =>
                    onAssignLesson(
                      lesson.id,
                      lesson.assigned_teacher_name ||
                        lesson.assigned_teacher_short_code
                    )
                  }
                >
                  {substitute ? changeLabel : assignLabel}
                </button>
              )}
              {!struck && onCancelLesson && (
                <TalkRadarLessonStorno
                  status={lesson.status}
                  filled={cancelFilled}
                  onAsk={() => onCancelLesson(lesson.id)}
                />
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}

const TAKEN_LESSON = new Set([
  "confirmed",
  "accepted",
  "assigned",
  "filled",
  "booked",
  "cancelled",
]);

function canOwnerRemove(lesson: LessonRow): boolean {
  const state = lesson.assignment_state || "";
  const status = lesson.status || "";
  return !TAKEN_LESSON.has(state) && !TAKEN_LESSON.has(status);
}

function initialsOf(name: string | null | undefined): string {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

function adminPrepMark(status: string | null | undefined): boolean {
  return (
    status === "complete" || status === "partial" || status === "prep_requested"
  );
}

function coveredShortCode(lesson: LessonRow): string | null {
  const state = lesson.assignment_state || lesson.status || "";
  const covered =
    state === "confirmed" ||
    state === "accepted" ||
    state === "booked" ||
    lesson.status === "booked" ||
    lesson.status === "confirmed";
  if (!covered) return null;
  return lesson.assigned_teacher_short_code || null;
}

function isDirectorRemoved(lesson: LessonRow): boolean {
  return (
    lesson.status === "director_removed" ||
    lesson.assignment_state === "director_removed"
  );
}

function isDeadAdminLesson(lesson: LessonRow): boolean {
  const dead = (value?: string | null) =>
    value === "cancelled" || value === "director_removed";
  return dead(lesson.status) || dead(lesson.assignment_state);
}

function AdminPeople({ admin }: { admin: AdminSubstitutionDetail }) {
  const t = useTalkT();
  const people =
    admin.assigned_teachers && admin.assigned_teachers.length > 0
      ? admin.assigned_teachers
      : admin.assigned_teacher_name
        ? [
            {
              teacher_id: admin.assigned_teacher_id || "assigned",
              teacher_name: admin.assigned_teacher_name,
              teacher_short_code: admin.assigned_teacher_short_code || "",
              lesson_count: admin.total_lesson_count,
            },
          ]
        : [];
  const circle = (name: string, color: string) => (
    <span
      aria-label={t("talk_radar_initials", { name })}
      style={{
        display: "inline-flex",
        width: 40,
        height: 40,
        borderRadius: "999px",
        alignItems: "center",
        justifyContent: "center",
        background: color,
        color: "#fff",
      }}
    >
      {initialsOf(name)}
    </span>
  );
  return (
    <>
      <section
        aria-label={t("talk_radar_participants")}
        style={{
          marginTop: 18,
          border: "1px solid #fbbf24",
          borderRadius: 16,
          overflow: "hidden",
        }}
      >
        <h3
          style={{
            margin: 0,
            padding: "10px 14px",
            background: "#fbbf24",
            color: "#1c1917",
            fontSize: 12,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          {t("talk_radar_participants")}
        </h3>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            padding: "12px 14px",
            background: "#111827",
            borderTop: "1px solid #1f2937",
          }}
        >
          {circle(admin.requesting_teacher_name, "#2563eb")}
          <div>
            <b style={{ display: "block" }}>{admin.requesting_teacher_name}</b>
            <small style={{ color: "#94a3b8" }}>
              <span>{t("talk_radar_requesting")}</span>
              {admin.requesting_teacher_short_code
                ? ` · ${admin.requesting_teacher_short_code}`
                : ""}
              {admin.requesting_teacher_faculty
                ? ` · ${admin.requesting_teacher_faculty}`
                : ""}
            </small>
          </div>
        </div>
        {people.map((person) => (
          <div
            key={person.teacher_id}
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              padding: "12px 14px",
              background: "#111827",
              borderTop: "1px solid #1f2937",
            }}
          >
            {circle(person.teacher_name, "#16a34a")}
            <div>
              <b style={{ display: "block" }}>{person.teacher_name}</b>
              <small style={{ color: "#94a3b8" }}>
                <span>{t("talk_radar_col_substitute")}</span>
                {person.teacher_short_code
                  ? ` · ${person.teacher_short_code}`
                  : ""}{" "}
                <span>{person.lesson_count}</span>
              </small>
            </div>
          </div>
        ))}
        {people.length === 0 && (
          <p style={{ margin: 0, padding: "12px 14px", background: "#111827" }}>
            {t("talk_radar_nobody_assigned")}
          </p>
        )}
      </section>
      <section aria-label="Antworten" style={{ marginTop: 24 }}>
        <h3>Antworten</h3>
        {admin.responses.length === 0 ? (
          <p>Noch keine Antworten</p>
        ) : (
          admin.responses.map((response, index) => (
            <p
              key={`${response.teacher_id || "r"}-${index}`}
              style={{
                marginTop: 16,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <span>{response.teacher_name}</span>
              <span>{answerLabel(response.response_type)}</span>
              {(response.reminder_count ?? 0) > 0 && <span>erinnert</span>}
              {response.response_message ? (
                <span>{response.response_message}</span>
              ) : null}
              {response.decline_reason ? (
                <span>{response.decline_reason}</span>
              ) : null}
            </p>
          ))
        )}
      </section>
    </>
  );
}

function fullDayDates(lessons: LessonRow[]): string[] {
  return Array.from(
    new Set(
      lessons
        .filter(
          (lesson) =>
            lesson.can_respond &&
            lesson.block_type === "full_day" &&
            lesson.date
        )
        .map((lesson) => (lesson.date || "").slice(0, 10))
    )
  ).sort();
}

function DayChoice({
  dates,
  lessons,
  ask,
  onAsk,
  onCancel,
  onConfirm,
}: {
  dates: string[];
  lessons: LessonRow[];
  ask: string | null;
  onAsk: (
    ask: string,
    items: { lesson_request_id: string; response: "accept" | "decline" }[]
  ) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const t = useTalkT();
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const ticked = Object.entries(selected)
    .filter(([, on]) => on)
    .map(([date]) => date);
  return (
    <div>
      {dates.map((date) => (
        <div key={date}>
          <label>
            <input
              type="checkbox"
              checked={!!selected[date]}
              onChange={() =>
                setSelected((prev) => ({ ...prev, [date]: !prev[date] }))
              }
            />
            <span>{formatDay(date)}</span>
          </label>{" "}
          <button
            type="button"
            onClick={() =>
              onAsk(
                t("talk_radar_day_decline_ask"),
                lessons
                  .filter(
                    (lesson) =>
                      lesson.can_respond &&
                      lesson.date &&
                      lesson.date.slice(0, 10) === date
                  )
                  .map((lesson) => ({
                    lesson_request_id: lesson.id,
                    response: "decline" as const,
                  }))
              )
            }
          >
            {t("talk_radar_day_decline")}
          </button>
        </div>
      ))}
      {ask ? (
        <>
          <span>{ask}</span>{" "}
          <button type="button" onClick={onConfirm}>
            Bestätigen
          </button>{" "}
          <button type="button" onClick={onCancel}>
            Abbrechen
          </button>
        </>
      ) : (
        <button
          type="button"
          disabled={ticked.length === 0}
          onClick={() =>
            onAsk(
              t("talk_radar_days_take_ask"),
              lessons
                .filter(
                  (lesson) =>
                    lesson.can_respond &&
                    lesson.block_type === "full_day" &&
                    lesson.date &&
                    ticked.includes(lesson.date.slice(0, 10))
                )
                .map((lesson) => ({
                  lesson_request_id: lesson.id,
                  response: "accept" as const,
                }))
            )
          }
        >
          {t("talk_radar_days_take")}
        </button>
      )}
      <p>{t("talk_radar_days_open")}</p>
    </div>
  );
}

export function TalkRadarDetail({
  role,
  requestId,
  kind = "incoming",
  requestStatus = null,
  requestDate = null,
  incoming,
  onAssignLesson,
  onStornoBack,
  onEdgeBack,
  onMessage,
}: {
  role: "admin" | "teacher";
  requestId: string | null;
  kind?: "incoming" | "own";
  requestStatus?: string | null;
  requestDate?: string | null;
  incoming?: boolean;
  onAssignLesson?: (
    lessonId: string,
    assignedTeacherName?: string | null
  ) => void;
  onStornoBack?: (abort: (() => void) | null) => void;
  onEdgeBack?: () => void;
  onMessage?: () => void;
}) {
  const t = useTalkT();
  const statusLabel = useRadarStatusLabel();
  const myTeacherId = useAuthStore((s) => s.user?.teacher_id);
  const loaded = useTalkRadarDetailLoad(role, requestId, kind);
  const respond = useTalkRadarRespond(requestId, loaded.setTeacherLessons);
  const storno = useTalkRadarStorno(requestId, loaded.admin, loaded.setAdmin);
  const { confirm, setConfirm } = storno;

  const [lessonStornoArmed, setLessonStornoArmed] = useState(false);
  const [markedCancelled, setMarkedCancelled] = useState(false);
  const [prepAsk, setPrepAsk] = useState(false);
  const [prepReason, setPrepReason] = useState("");
  const [prepAsked, setPrepAsked] = useState(false);
  const [prepClosed, setPrepClosed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [teacherAct, setTeacherAct] = useState<
    null | "cancel" | "remind" | "message"
  >(null);
  const [actText, setActText] = useState("");
  const [actNotice, setActNotice] = useState<string | null>(null);
  const [prepOpen, setPrepOpen] = useState<{
    lessons: { id: string; subject: string; time: string; note: string }[];
    notes: string[];
    messages: { id: string; sender: string; body: string }[];
  } | null>(null);
  useEffect(() => {
    setLessonStornoArmed(false);
    setPrepAsked(false);
    setPrepClosed(false);
    setPrepAsk(false);
    setPrepReason("");
    setPrepOpen(null);
    setMarkedCancelled(false);
  }, [requestId]);
  useEffect(() => {
    if (!onStornoBack) return;
    onStornoBack(confirm ? () => setConfirm(null) : null);
    return () => onStornoBack(null);
  }, [onStornoBack, confirm, setConfirm]);
  const lessons: LessonRow[] =
    role === "admin"
      ? (loaded.admin?.lessons || []).map((lesson) => ({
          id: lesson.id,
          date: lesson.date,
          time_slot_name: lesson.time_slot_name,
          subject_name: lesson.subject_name,
          klass_name: lesson.klass_name,
          room_name: lesson.room_name,
          assigned_teacher_id: lesson.assigned_teacher_id,
          assigned_teacher_name: lesson.assigned_teacher_name,
          assigned_teacher_short_code: lesson.assigned_teacher_short_code,
          status: lesson.status,
          assignment_state:
            (lesson as { assignment_state?: string | null }).assignment_state ||
            lesson.status,
        }))
      : teacherLessonRows(loaded.teacherLessons);
  const directorOn = useDirectorEnabled();
  const note =
    loaded.teacher && kind !== "own" ? teacherNote(loaded.teacher) : null;
  const loadedStatus =
    kind === "own" ? loaded.ownRequest?.status : loaded.teacher?.status;
  const loadedDate =
    (kind === "own" ? loaded.ownRequest?.date : loaded.teacher?.date) ||
    lessons.find((lesson) => lesson.date)?.date ||
    null;
  const ownDate = loadedDate || requestDate || null;
  const ownStatus = markedCancelled
    ? "cancelled"
    : loadedStatus || requestStatus || "";
  const error = storno.error || loaded.error;
  const takenLessons = lessons.filter((lesson) =>
    getTakenOverLessonIds([lesson], myTeacherId).includes(lesson.id)
  );
  const basePrep =
    kind === "own" ? loaded.ownPrep : loaded.teacher?.prep_summary;
  const prepSummary = prepAsked
    ? {
        has_prep: basePrep?.has_prep ?? false,
        lessons_with_prep: basePrep?.lessons_with_prep ?? 0,
        total_lessons: basePrep?.total_lessons ?? 0,
        prep_titles: basePrep?.prep_titles ?? [],
        has_open_prep_request: true,
      }
    : prepClosed
      ? {
          ...basePrep,
          has_prep: basePrep?.has_prep ?? false,
          lessons_with_prep: basePrep?.lessons_with_prep ?? 0,
          total_lessons: basePrep?.total_lessons ?? 0,
          prep_titles: basePrep?.prep_titles ?? [],
          has_open_prep_request: false,
        }
      : basePrep;
  const canAskPrep =
    role === "teacher" &&
    kind !== "own" &&
    takenLessons.length > 0 &&
    !prepSummary?.has_open_prep_request;
  const submitPrepAsk = async () => {
    if (!requestId) return;
    const reason = prepReason.trim();
    await teacherPriorityApi.requestPrep(requestId, {
      lesson_request_ids: takenLessons.map((lesson) => lesson.id),
      reason: reason || undefined,
    });
    setPrepAsked(true);
    setPrepAsk(false);
    setPrepReason("");
  };
  const closePrep = async () => {
    if (!requestId) return;
    const requests = await teacherPriorityApi.getPrepRequests(requestId);
    for (const item of requests.filter(
      (entry) => entry.status === "requested"
    )) {
      await teacherPriorityApi.resolvePrepRequest(requestId, item.id, {
        status: "fulfilled",
      });
    }
    setPrepClosed(true);
    setPrepAsked(false);
  };
  const openPrep = async () => {
    if (!requestId) return;
    const [lessonStatus, preps, chat] = await Promise.all([
      teacherPriorityApi.getLessonPrepStatus(requestId),
      teacherPriorityApi.getPreps(requestId),
      teacherPriorityApi.getChatMessages(requestId),
    ]);
    setPrepOpen({
      lessons: lessonStatus.map((lesson) => ({
        id: lesson.lesson_request_id,
        subject: lesson.subject_name || "—",
        time: lesson.time_slot_name || "—",
        note:
          preps
            .find((prep) =>
              prep.linked_lessons?.some(
                (link) => link.lesson_request_id === lesson.lesson_request_id
              )
            )
            ?.notes?.trim() || "",
      })),
      notes: preps
        .filter((prep) => !prep.linked_lessons?.length)
        .map((prep) => prep.notes?.trim() || "")
        .filter(Boolean),
      messages: (chat.messages || []).map((message) => ({
        id: message.id,
        sender: message.sender_name,
        body: message.body,
      })),
    });
  };
  const dayChoice = role === "teacher" && kind !== "own" && incoming === true;
  const dayDates = dayChoice ? fullDayDates(lessons) : [];
  const liveLessons = lessons.filter((lesson) => !isDeadAdminLesson(lesson));
  const requestCancelled = loaded.admin?.status === "cancelled";
  const showDock =
    role === "admin" && Boolean(loaded.admin) && !requestCancelled;
  const showLessonCancel =
    role === "admin" &&
    lessonStornoArmed &&
    liveLessons.length > 1 &&
    !requestCancelled;
  const detail = (
    <>
      {error && <p>{error}</p>}
      {respond.actionError && <p>{respond.actionError}</p>}
      {role === "teacher" && kind === "own" && (
        <header className="talk-dcard-head">
          <h2>{formatDay(ownDate)}</h2>
          {ownStatus ? (
            <span className="talk-pill">{statusLabel(ownStatus)}</span>
          ) : null}
          {onEdgeBack && (
            <button
              type="button"
              className="talk-card-close"
              onClick={onEdgeBack}
            >
              Schliessen
            </button>
          )}
        </header>
      )}
      {role === "teacher" && kind !== "own" && loaded.teacher && (
        <header className="talk-dcard-head">
          <h2>{loaded.teacher.requesting_teacher_name}</h2>
          {loaded.teacher.requesting_teacher_short_code ? (
            <span className="talk-pill is-code">
              {loaded.teacher.requesting_teacher_short_code}
            </span>
          ) : null}
          <span className="talk-pill">
            {statusLabel(loaded.teacher.status)}
          </span>
          {onEdgeBack && (
            <button
              type="button"
              className="talk-card-close"
              onClick={onEdgeBack}
            >
              Schliessen
            </button>
          )}
        </header>
      )}
      {role === "teacher" &&
        kind === "own" &&
        requestId &&
        loaded.ownRequest && (
          <TalkOwnDesktop
            requestId={requestId}
            request={loaded.ownRequest}
            fileInputRef={fileInputRef}
          />
        )}
      {role === "teacher" && kind !== "own" && requestId && loaded.teacher && (
        <TalkIncomingFinance
          requestId={requestId}
          request={loaded.teacher}
          directorOn={directorOn}
        />
      )}
      {role === "teacher" && (kind === "own" || loaded.teacher) && (
        <TalkPrepBand summary={prepSummary}>
          {canAskPrep && (
            <button type="button" onClick={() => setPrepAsk(true)}>
              {t("talk_radar_prep_ask")}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              void openPrep();
            }}
          >
            {t("talk_radar_prep_open")}
          </button>
          {kind === "own" && prepSummary?.has_open_prep_request && (
            <button
              type="button"
              onClick={() => {
                void closePrep();
              }}
            >
              {t("talk_radar_prep_done")}
            </button>
          )}
        </TalkPrepBand>
      )}
      {prepOpen && (
        <section aria-label="Prep">
          <h3>Lektionen</h3>
          <ul>
            {prepOpen.lessons.map((lesson) => (
              <li key={lesson.id}>
                <span>{lesson.subject}</span> <span>{lesson.time}</span>
                {lesson.note ? <span>{lesson.note}</span> : null}
              </li>
            ))}
          </ul>
          {prepOpen.notes.map((note) => (
            <p key={note}>{note}</p>
          ))}
          <h3>Chat</h3>
          <ul>
            {prepOpen.messages.map((message) => (
              <li key={message.id}>
                <span>{message.sender}</span> <span>{message.body}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {prepAsk && (
        <div role="dialog" aria-label="Prep anfordern">
          <ul>
            {takenLessons.map((lesson) => (
              <li key={lesson.id}>
                <span>{lesson.subject_name || "—"}</span>{" "}
                <span>{lesson.time_slot_name || "—"}</span>
              </li>
            ))}
          </ul>
          <label>
            Grund (optional)
            <textarea
              value={prepReason}
              onChange={(event) => setPrepReason(event.target.value)}
            />
          </label>
          <button
            type="button"
            onClick={() => {
              void submitPrepAsk();
            }}
          >
            Anfordern
          </button>
          <button
            type="button"
            onClick={() => {
              setPrepAsk(false);
              setPrepReason("");
            }}
          >
            Abbrechen
          </button>
        </div>
      )}
      {dayDates.length > 0 && (
        <DayChoice
          dates={dayDates}
          lessons={lessons}
          ask={respond.dayAsk?.ask ?? null}
          onAsk={(ask, items) => respond.askDay({ ask, items })}
          onCancel={respond.cancelDay}
          onConfirm={() => {
            void respond.confirmDay();
          }}
        />
      )}
      {role === "admin" && loaded.admin && (
        <header className="talk-dcard-head">
          <h2>{loaded.admin.requesting_teacher_name}</h2>
          <span className="talk-pill">{statusLabel(loaded.admin.status)}</span>
          {onEdgeBack && (
            <button
              type="button"
              className="talk-card-close"
              onClick={onEdgeBack}
            >
              Schliessen
            </button>
          )}
          {onMessage && (
            <button type="button" onClick={onMessage}>
              {t("talk_radar_message")}
            </button>
          )}
        </header>
      )}
      {role === "admin" &&
        (requestCancelled ||
          lessons.some(
            (lesson) =>
              lesson.status === "cancelled" ||
              lesson.assignment_state === "cancelled"
          )) && (
          <p>
            {`${t("talk_radar_banner_cancelled")} ${
              lessons.filter(
                (lesson) =>
                  lesson.status === "cancelled" ||
                  lesson.assignment_state === "cancelled"
              ).length
            }/${lessons.length}`}
          </p>
        )}
      {role === "admin" ? (
        <LessonTable
          adminRead
          lessons={lessons}
          myTeacherId={myTeacherId}
          pending={null}
          onChoose={() => undefined}
          onCancel={() => undefined}
          onConfirm={() => undefined}
          onAssignLesson={onAssignLesson}
          onCancelLesson={
            showLessonCancel
              ? (lessonId) => storno.setConfirm({ kind: "lesson", lessonId })
              : undefined
          }
          assignLabel={t("talk_radar_assign")}
          changeLabel={t("talk_radar_change")}
          headers={{
            date: t("talk_radar_col_date"),
            time: t("talk_radar_col_time"),
            subject: t("talk_radar_col_subject"),
            klass: t("talk_radar_col_class"),
            action: t("talk_radar_col_action"),
          }}
        />
      ) : (
        <LessonTable
          lessons={lessons}
          owner={kind === "own"}
          teacherActions
          packageHint={dayChoice}
          myTeacherId={myTeacherId}
          pending={respond.pending}
          onChoose={respond.choose}
          onCancel={respond.cancel}
          onConfirm={() => {
            void respond.confirmChoice();
          }}
          onWithdraw={
            kind !== "own" && requestId
              ? (lessonId) => {
                  void teacherApi.unDeclineLesson(requestId, lessonId);
                }
              : undefined
          }
          hrOn={financeUiEnabled(
            kind === "own"
              ? loaded.ownRequest?.hr_module_enabled
              : loaded.teacher?.hr_module_enabled
          )}
          onTeacherStorno={
            kind !== "own" && requestId
              ? (lessonId) => {
                  void teacherPriorityApi.createCancellationRequest({
                    substitution_request_id: requestId,
                    lesson_ids: [lessonId],
                  });
                }
              : undefined
          }
          onRemoveLesson={
            role === "teacher" &&
            kind === "own" &&
            requestId &&
            ownStatus !== "cancelled"
              ? (lessonId) => {
                  void teacherPriorityApi
                    .removeLesson(requestId, lessonId)
                    .then(() => {
                      loaded.setTeacherLessons((current) =>
                        current.filter((lesson) => lesson.id !== lessonId)
                      );
                    });
                }
              : undefined
          }
          assignLabel={t("talk_radar_assign")}
          changeLabel={t("talk_radar_change")}
          headers={{
            date: t("talk_radar_col_date"),
            time: t("talk_radar_col_time"),
            subject: t("talk_radar_col_subject"),
            klass: t("talk_radar_col_class"),
            action: t("talk_radar_col_action"),
          }}
        />
      )}
      {role === "teacher" &&
        requestId &&
        (kind === "own" || loaded.teacher) &&
        ownStatus !== "cancelled" && (
          <div className="talk-teacher-acts">
            {kind === "own" && (
              <button
                type="button"
                className="kill"
                onClick={() => setTeacherAct("cancel")}
              >
                {t("talk_radar_storno")}
              </button>
            )}
            {kind === "own" && (
              <button
                type="button"
                className="remind"
                onClick={() => setTeacherAct("remind")}
              >
                {t("talk_radar_remind")}
              </button>
            )}
            <button
              type="button"
              className="msg"
              onClick={() => setTeacherAct("message")}
            >
              {t("talk_radar_message")}
            </button>
          </div>
        )}
      {teacherAct === "cancel" && requestId && (
        <div role="dialog" aria-label={t("talk_radar_storno")}>
          <p>{t("talk_radar_storno_request_ask")}</p>
          <button
            type="button"
            onClick={() => {
              void teacherPriorityApi.cancelRequest(requestId).then(() => {
                setMarkedCancelled(true);
                loaded.setTeacherLessons((current) =>
                  current.map((lesson) => ({
                    ...lesson,
                    status: "cancelled",
                    assignment_state: "cancelled",
                  }))
                );
                setTeacherAct(null);
                setActNotice(t("talk_radar_status_cancelled"));
              });
            }}
          >
            {t("talk_radar_confirm")}
          </button>
          <button type="button" onClick={() => setTeacherAct(null)}>
            {t("talk_radar_cancel")}
          </button>
        </div>
      )}
      {teacherAct === "remind" && requestId && (
        <div role="dialog" aria-label={t("talk_radar_remind")}>
          <h2>{t("talk_radar_remind_ask")}</h2>
          <textarea
            aria-label={t("talk_radar_remind")}
            value={actText}
            placeholder={t("talk_radar_remind_placeholder")}
            onChange={(event) => setActText(event.target.value)}
          />
          <button
            type="button"
            onClick={() => {
              const ids = lessons.map((lesson) => lesson.id);
              void teacherApi
                .sendRadarReminder(ids, actText || undefined)
                .then(() => {
                  setTeacherAct(null);
                  setActText("");
                  setActNotice(t("talk_radar_remind"));
                });
            }}
          >
            {t("talk_radar_send")}
          </button>
          <button type="button" onClick={() => setTeacherAct(null)}>
            {t("talk_radar_cancel")}
          </button>
        </div>
      )}
      {teacherAct === "message" && requestId && (
        <div role="dialog" aria-label={t("talk_radar_message")}>
          <h2>{t("talk_radar_message")}</h2>
          <textarea
            aria-label={t("talk_radar_message")}
            value={actText}
            onChange={(event) => setActText(event.target.value)}
          />
          <button
            type="button"
            onClick={() => {
              const body = actText.trim();
              if (!body) return;
              void teacherPriorityApi
                .sendChatMessage(requestId, body)
                .then(() => {
                  setTeacherAct(null);
                  setActText("");
                });
            }}
          >
            {t("talk_radar_send")}
          </button>
          <button type="button" onClick={() => setTeacherAct(null)}>
            {t("talk_radar_cancel")}
          </button>
        </div>
      )}
      {actNotice && <p>{actNotice}</p>}
      {storno.confirm && (
        <TalkRadarStornoConfirm
          confirm={storno.confirm}
          lastLive={
            lessons.filter((lesson) => !isDeadAdminLesson(lesson)).length === 1
          }
          onAbort={() => storno.setConfirm(null)}
          onCommit={(message) => {
            void storno.commit(message);
          }}
        />
      )}
      {storno.notice && <p>{storno.notice}</p>}
      {showDock && (
        <TalkRadarDock
          showCancel={liveLessons.length > 0}
          onRemind={() => storno.setConfirm({ kind: "remind" })}
          onCancelRequest={() => storno.setConfirm({ kind: "request" })}
          onCancelLessons={() => {
            const only = liveLessons.length === 1 ? liveLessons[0] : undefined;
            if (only) {
              storno.setConfirm({ kind: "lesson", lessonId: only.id });
              return;
            }
            setLessonStornoArmed(true);
          }}
        />
      )}
      {role === "admin" &&
        loaded.admin &&
        adminPrepMark(loaded.admin.prep?.status) && <p>Prep</p>}
      {role === "admin" && loaded.admin && (
        <>
          <AdminPeople admin={loaded.admin} />
        </>
      )}
      {role === "teacher" && loaded.teacher && note && (
        <p>
          <span>Notiz</span> <span>{note}</span>
        </p>
      )}
    </>
  );
  return (
    <div className="talk-teacher-stage">
      <article className="talk-dcard">{detail}</article>
    </div>
  );
}
